import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Sparkles } from "lucide-react";

/**
 * Batch 21 — Analytics Settings.
 *
 * Super-admin editable GA4 Measurement ID and Microsoft Clarity Project ID.
 * Server-side stored (never hardcoded, never in the build bundle). Public
 * loader picks these up on first paint. If empty/disabled, no third-party
 * script is injected and the public site remains completely untouched.
 */
export default function AdminAnalyticsSettings() {
    const [cfg, setCfg] = useState({
        ga4_measurement_id: "",
        ga4_enabled: false,
        clarity_project_id: "",
        clarity_enabled: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/analytics-config");
            setCfg({
                ga4_measurement_id: data.ga4_measurement_id || "",
                ga4_enabled: !!data.ga4_enabled,
                clarity_project_id: data.clarity_project_id || "",
                clarity_enabled: !!data.clarity_enabled,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Failed to load settings");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function save(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/analytics-config", cfg);
            toast.success("Analytics settings saved.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Save failed");
        } finally { setSaving(false); }
    }

    return (
        <div data-testid="admin-analytics-settings" className="max-w-3xl">
            <div className="gsf-eyebrow">Analytics Settings</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="analytics-settings-title">
                Third-Party Analytics
            </h1>
            <p className="mt-3 text-sm text-gray-600">
                Optional integrations. Enter and enable each ID below to start collecting anonymised
                visitor insights. Leave empty or disabled to keep the public site free of any
                third-party analytics script.
            </p>

            {loading ? <p className="mt-6 text-sm text-gray-500">Loading…</p> : (
                <form onSubmit={save} className="mt-6 space-y-6" data-testid="analytics-settings-form">
                    <div className="gsf-card p-6">
                        <div className="flex items-center gap-2">
                            <Sparkles size={18} className="text-[color:var(--gsf-green-700)]" strokeWidth={1.8} />
                            <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Google Analytics 4</h2>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            Measurement ID format: <code className="font-mono">G-XXXXXXXXXX</code>. Retrieve it from
                            <em> Admin → Data streams → Web stream details </em> in your GA4 property.
                        </p>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">GA4 Measurement ID</label>
                                <input
                                    value={cfg.ga4_measurement_id}
                                    onChange={(e) => setCfg({ ...cfg, ga4_measurement_id: e.target.value })}
                                    placeholder="G-XXXXXXXXXX"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono bg-white"
                                    data-testid="ga4-id-input"
                                />
                            </div>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={cfg.ga4_enabled}
                                    onChange={(e) => setCfg({ ...cfg, ga4_enabled: e.target.checked })}
                                    data-testid="ga4-enabled-toggle"
                                />
                                Enable GA4
                            </label>
                        </div>
                    </div>

                    <div className="gsf-card p-6">
                        <div className="flex items-center gap-2">
                            <Sparkles size={18} className="text-[color:var(--gsf-green-700)]" strokeWidth={1.8} />
                            <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Microsoft Clarity</h2>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            Project ID from <em>Clarity → Settings → Overview</em>. Session recording and heatmaps
                            respect Clarity’s default privacy masking.
                        </p>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Clarity Project ID</label>
                                <input
                                    value={cfg.clarity_project_id}
                                    onChange={(e) => setCfg({ ...cfg, clarity_project_id: e.target.value })}
                                    placeholder="abcd1234ef"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono bg-white"
                                    data-testid="clarity-id-input"
                                />
                            </div>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={cfg.clarity_enabled}
                                    onChange={(e) => setCfg({ ...cfg, clarity_enabled: e.target.checked })}
                                    data-testid="clarity-enabled-toggle"
                                />
                                Enable Clarity
                            </label>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                            No IDs are stored in the frontend bundle. Changes take effect on next page load.
                        </p>
                        <button
                            type="submit"
                            className="gsf-btn-primary text-sm px-6 disabled:opacity-60"
                            disabled={saving}
                            data-testid="analytics-settings-save"
                        >
                            <Save size={14} /> {saving ? "Saving…" : "Save settings"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
